import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

/**
 * NetworkSphere — canvas-rendered wireframe sphere with signal traversal.
 *
 * Geometry: ~70 nodes distributed via a Fibonacci spiral on a unit sphere,
 * each connected to its k-nearest neighbors (Euclidean) to form an edge graph.
 *
 * Render: rotated each frame around Y (yaw) and X (pitch) and projected to 2D.
 * Edges drawn as muted strokes with alpha that brightens for front-facing
 * vertices.  Periodically a "signal" is spawned: a random walk of N hops along
 * the edge graph that lights up each traversed segment with a glowing stroke
 * and pulses the active node before fading out.
 *
 * Controlled via props:
 *   - speed (rad/s, base rotation)
 *   - signalIntervalMs (base spawn cadence)
 *   - hoverIntensify (multiplier applied while hoverActiveRef.current === true)
 *   - hoverActiveRef (mutable ref so we don't tear down the RAF loop on hover)
 *   - reducedMotion (disables signals + slows rotation when true)
 *   - onSignalSpawn  (fires when a new signal begins)
 */

// ─── Tunables (also exported so the parent can tweak) ───────────────────────
export const SPHERE_TUNING = {
  nodeCount: 70,
  neighborCount: 4,        // edges per node (each undirected edge counted once)
  edgeMs: 620,             // duration per signal hop (slightly slower flow)
  signalLengthMin: 7,
  signalLengthMax: 11,
  fadeOutMs: 1100,         // tail fade after signal completes
};

// Generate N points on a unit sphere via Fibonacci distribution.
const makeSphereNodes = (n) => {
  const phi = Math.PI * (3 - Math.sqrt(5));
  const out = [];
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const theta = phi * i;
    out.push({ x: Math.cos(theta) * r, y, z: Math.sin(theta) * r });
  }
  return out;
};

// For each node, link to its k nearest neighbors. Returns adjacency list and
// an undirected edge list with no duplicates.
const buildEdges = (nodes, k) => {
  const adj = nodes.map(() => []);
  const edgeSet = new Set();
  const edges = [];
  for (let i = 0; i < nodes.length; i++) {
    const dists = [];
    for (let j = 0; j < nodes.length; j++) {
      if (i === j) continue;
      const dx = nodes[i].x - nodes[j].x;
      const dy = nodes[i].y - nodes[j].y;
      const dz = nodes[i].z - nodes[j].z;
      dists.push({ j, d: dx * dx + dy * dy + dz * dz });
    }
    dists.sort((a, b) => a.d - b.d);
    for (let m = 0; m < k; m++) {
      const j = dists[m].j;
      if (!adj[i].includes(j)) adj[i].push(j);
      if (!adj[j].includes(i)) adj[j].push(i);
      const key = i < j ? `${i},${j}` : `${j},${i}`;
      if (!edgeSet.has(key)) {
        edgeSet.add(key);
        edges.push([Math.min(i, j), Math.max(i, j)]);
      }
    }
  }
  return { adj, edges };
};

const Wrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  display: block;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`;

// Theme-aware colour channels for canvas drawing.
// On dark theme the sphere is white-ish on a dark page; on light theme it
// flips to dark strokes on a light page. Signals stay cyan-tinted in both.
const FG_CHANNELS = {
  dark:  { r: 255, g: 255, b: 255 },
  light: { r: 10,  g: 12,  b: 16  },
};
const SIGNAL_GLOW = {
  dark:  { r: 180, g: 220, b: 255 },
  light: { r: 60,  g: 130, b: 200 },
};
const SIGNAL_LINE = {
  dark:  { r: 225, g: 240, b: 255 },
  light: { r: 30,  g: 90,  b: 170 },
};

const NetworkSphere = ({
  speed = 0.07,
  signalIntervalMs = 1600,
  hoverIntensify = 1.9,
  hoverActiveRef,
  reducedMotion = false,
  theme = 'dark',
  // Each frame, the loop writes current 2D-projected positions of all
  // nodes into nodePositionsRef.current[i] = {x, y, z} (CSS pixels relative
  // to this canvas). Lets external components (SphereWidgets) anchor UI
  // elements to specific nodes that follow the sphere's rotation.
  nodePositionsRef,
  onSignalSpawn,
}) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  // Latest-prop refs so RAF loop reads current values without restart
  const reducedMotionRef = useRef(reducedMotion);
  const themeRef = useRef(theme);
  const onSignalSpawnRef = useRef(onSignalSpawn);
  useEffect(() => { reducedMotionRef.current = reducedMotion; }, [reducedMotion]);
  useEffect(() => { themeRef.current = theme; }, [theme]);
  useEffect(() => { onSignalSpawnRef.current = onSignalSpawn; }, [onSignalSpawn]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Build geometry once per mount.
    const nodes3D = makeSphereNodes(SPHERE_TUNING.nodeCount);
    const { adj, edges } = buildEdges(nodes3D, SPHERE_TUNING.neighborCount);

    const state = {
      yaw: 0,
      pitch: -0.18,
      signals: [],
      lastSpawn: 0,
      cssWidth: 0,
      cssHeight: 0,
    };

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      state.cssWidth = rect.width;
      state.cssHeight = rect.height;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    resize();

    // Spawn a signal: random walk of length 4–6 along the edge graph.
    const spawnSignal = (now) => {
      const startIdx = Math.floor(Math.random() * nodes3D.length);
      const path = [startIdx];
      const len =
        SPHERE_TUNING.signalLengthMin +
        Math.floor(Math.random() * (SPHERE_TUNING.signalLengthMax - SPHERE_TUNING.signalLengthMin + 1));
      let cur = startIdx;
      for (let i = 0; i < len; i++) {
        const neighbors = adj[cur].filter((n) => !path.includes(n));
        if (!neighbors.length) break;
        const next = neighbors[Math.floor(Math.random() * neighbors.length)];
        path.push(next);
        cur = next;
      }
      if (path.length < 2) return;
      state.signals.push({
        path,
        startTime: now,
        edgeMs: SPHERE_TUNING.edgeMs,
      });
      if (onSignalSpawnRef.current) onSignalSpawnRef.current(path.length);
    };

    let last = performance.now();
    let raf;
    const loop = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      const reduced = reducedMotionRef.current;
      const hoverActive = !!(hoverActiveRef && hoverActiveRef.current);
      const themeKey = themeRef.current === 'light' ? 'light' : 'dark';
      const fg = FG_CHANNELS[themeKey];
      const sigGlow = SIGNAL_GLOW[themeKey];
      const sigLine = SIGNAL_LINE[themeKey];

      // Rotation (slowed under reduced motion, slightly accelerated on hover)
      const speedMul = reduced ? 0.2 : 1 + (hoverActive ? 0.35 : 0);
      state.yaw += dt * speed * speedMul;

      // Signal spawn cadence
      if (!reduced) {
        const intensify = 1 + (hoverActive ? hoverIntensify - 1 : 0);
        const interval = signalIntervalMs / intensify;
        if (now - state.lastSpawn > interval) {
          spawnSignal(now);
          state.lastSpawn = now;
        }
      }

      // Project nodes 3D → 2D
      const cx = state.cssWidth / 2;
      const cy = state.cssHeight / 2;
      const radius = Math.min(cx, cy) * 0.86;
      const cosY = Math.cos(state.yaw);
      const sinY = Math.sin(state.yaw);
      const cosP = Math.cos(state.pitch);
      const sinP = Math.sin(state.pitch);

      const projected = new Array(nodes3D.length);
      for (let i = 0; i < nodes3D.length; i++) {
        const n = nodes3D[i];
        const x1 = n.x * cosY + n.z * sinY;
        const z1 = -n.x * sinY + n.z * cosY;
        const y2 = n.y * cosP - z1 * sinP;
        const z2 = n.y * sinP + z1 * cosP;
        projected[i] = { x: cx + x1 * radius, y: cy + y2 * radius, z: z2 };
      }

      // Publish projected positions for any external follower (e.g. the
      // pipeline widgets that anchor to a node and ride along with rotation).
      if (nodePositionsRef && nodePositionsRef.current) {
        for (let i = 0; i < projected.length; i++) {
          nodePositionsRef.current[i] = projected[i];
        }
      }

      // Clear
      ctx.clearRect(0, 0, state.cssWidth, state.cssHeight);

      // ── Edges (muted base layer) ──
      ctx.lineWidth = 0.6;
      ctx.shadowBlur = 0;
      for (let e = 0; e < edges.length; e++) {
        const [a, b] = edges[e];
        const pa = projected[a];
        const pb = projected[b];
        const zAvg = (pa.z + pb.z) / 2;          // -1..1
        const t = (zAvg + 1) / 2;                 // 0..1
        const alpha = 0.06 + t * 0.28;
        ctx.strokeStyle = `rgba(${fg.r}, ${fg.g}, ${fg.b}, ${alpha})`;
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      }

      // ── Signals (lit edges + leading node pulse) ──
      ctx.lineCap = 'round';
      const stillAlive = [];
      for (let s = 0; s < state.signals.length; s++) {
        const sig = state.signals[s];
        const totalEdges = sig.path.length - 1;
        const elapsed = now - sig.startTime;
        const totalDuration = totalEdges * sig.edgeMs;
        if (elapsed > totalDuration + SPHERE_TUNING.fadeOutMs) continue; // expired

        const edgeIdx = Math.min(Math.floor(elapsed / sig.edgeMs), totalEdges - 1);
        const edgeT = (elapsed - edgeIdx * sig.edgeMs) / sig.edgeMs;

        // Compute decay for trailing edges (most recent = brightest)
        for (let e = 0; e <= Math.min(edgeIdx, totalEdges - 1); e++) {
          const a = sig.path[e];
          const b = sig.path[e + 1];
          const pa = projected[a];
          const pb = projected[b];
          // Recency: 1.0 at active edge, decreasing for trailing
          let strength;
          if (e === edgeIdx) {
            strength = 1;
          } else {
            strength = Math.max(0, 1 - (edgeIdx - e) * 0.32);
          }
          // Tail fade after signal completes its last edge
          if (elapsed > totalDuration) {
            const fade = 1 - (elapsed - totalDuration) / SPHERE_TUNING.fadeOutMs;
            strength *= Math.max(0, fade);
          }
          if (strength <= 0.01) continue;

          ctx.shadowBlur = 10;
          ctx.shadowColor = `rgba(${sigGlow.r}, ${sigGlow.g}, ${sigGlow.b}, ${strength * 0.7})`;
          ctx.strokeStyle = `rgba(${sigLine.r}, ${sigLine.g}, ${sigLine.b}, ${strength})`;
          ctx.lineWidth = 1.3;
          ctx.beginPath();
          if (e === edgeIdx && elapsed <= totalDuration) {
            // Active edge: only draw progress so far
            ctx.moveTo(pa.x, pa.y);
            ctx.lineTo(pa.x + (pb.x - pa.x) * edgeT, pa.y + (pb.y - pa.y) * edgeT);
          } else {
            ctx.moveTo(pa.x, pa.y);
            ctx.lineTo(pb.x, pb.y);
          }
          ctx.stroke();
        }

        // Pulse the leading node (the one the signal is reaching)
        if (elapsed <= totalDuration) {
          const leadIdx = sig.path[edgeIdx + 1];
          const p = projected[leadIdx];
          const pulse = 2.2 + Math.sin(elapsed * 0.018) * 0.7;
          ctx.shadowBlur = 14;
          ctx.shadowColor = `rgba(${sigGlow.r}, ${sigGlow.g}, ${sigGlow.b}, 0.85)`;
          ctx.fillStyle = `rgba(${sigLine.r}, ${sigLine.g}, ${sigLine.b}, 0.95)`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, pulse, 0, Math.PI * 2);
          ctx.fill();
        }

        stillAlive.push(sig);
      }
      ctx.shadowBlur = 0;
      state.signals = stillAlive;

      // ── Base node dots ──
      for (let i = 0; i < projected.length; i++) {
        const p = projected[i];
        const t = (p.z + 1) / 2;
        const alpha = 0.22 + t * 0.55;
        ctx.fillStyle = `rgba(${fg.r}, ${fg.g}, ${fg.b}, ${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
    // hoverActiveRef and props read via refs — effect runs once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speed, signalIntervalMs, hoverIntensify]);

  return (
    <Wrap ref={wrapRef}>
      <Canvas ref={canvasRef} />
    </Wrap>
  );
};

export default React.memo(NetworkSphere);
